import { useState, useEffect } from 'react';
import ReactPlayer from 'react-player/vimeo';
import { Typography, Button, Collapse, } from 'antd';
import { PlayCircleOutlined, ArrowRightOutlined, DownOutlined, UpOutlined} from '@ant-design/icons';
import "./heartFlow.scss";
import { CtaVideos } from '../ctaVideos/ctaVideos.component';
import ProductTypeCode from '../../enums/productTypeCode.enum';
import { useContext } from 'react';
import { UserContext } from '../../contexts/user.context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FlowType from '../../enums/flowType.enum';
import useWidth from '../../hooks/useWidth.hook';
import Breakpoint from '../../enums/breakpoint.enum';
import PosthogHelper from '../../helpers/posthog.helper';
import { FacilitySearch } from '../facilitySearch/facilitySearch.component';
import { listFaqs } from '../../services/faq.service';
import { FaqCode } from '../../enums/index.enum';
import Questions from '../questions/questions.component';

const { Paragraph, Text } = Typography;

export const HeartFlow = () => {
  const width = useWidth()
  const { currentUser } = useContext(UserContext)
  const [expandedParagraphs, setExpandedParagraphs] = useState({});
  const [videosDrawerVisible, setVideosDrawerVisible] = useState(false);
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [faqs, setFaqs] = useState([])

  useEffect(() => {
    document.title = `Instalab | HeartFlow: Coronary CT Angiogram`
    autoOpenVideos()
  }, [])


  useEffect(() => {
    const fetchFaqs = async () => {
      const filter = {
        code: { 
          $in: [
            FaqCode.CCTA_WHAT_IS, 
            FaqCode.CCTA_AGE, 
            FaqCode.CCTA_HOW_OFTEN, 
            FaqCode.CCTA_PRICE_COMPARISON, 
            FaqCode.CCTA_BETTER_TEST, 
            FaqCode.CCTA_LONGEVITY
          ]   
        }
      }
      const response = await listFaqs({filter})
      setFaqs(response)
    }

    fetchFaqs()
  }, [])


  const autoOpenVideos = () => {
    const video = searchParams.get('video')
    if (video === 'true') {
      setVideosDrawerVisible(true)
    }
  }
  
  const showVideosDrawer = () => {
    setVideosDrawerVisible(true);
  };
  
  const closeVideosDrawer = () => {
    setVideosDrawerVisible(false);
  };
  
  // Toggle paragraph expansion
  const toggleParagraph = (id) => {
    setExpandedParagraphs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const howContent = [
    {
      id: 'how-1',
      title: `Step 1: Capture High-Resolution Images of Your Heart with CT Scan`,
      short: "To highlight the plaque in your coronary arteries, a contrast dye is injected into a vein, typically your arm. You may also receive medication to slow your heart rate and improve the clarity of the CT images.",
      full: <>
        To highlight the plaque in your coronary arteries, a contrast dye is injected into a vein, typically your arm. You may also receive medication to slow your heart rate and improve the clarity of the CT images.
        <br/><br/>
        Once ready, you'll lie on a motorized table that slides into the CT scanner. As the machine rotates around you, it captures hundreds of detailed cross-sectional images of your heart within seconds. While the scan itself is quick, expect to spend up to an hour at the imaging center for preparation and post-scan procedures.
      </>,
      img: "/img/ct/heartflow-ct-scan.jpg"
    },
    {
      id: 'how-2',
      title: <>Step 2: HeartFlow FFR<sub>CT</sub> + Plaque Analysis</>,
      short: "After your scan, your CT images are securely uploaded to HeartFlow’s cloud platform. Their advanced software analyzes your CT images to assess both plaque and blood flow.",
      full: <>
        After your scan, your CT images are securely uploaded to HeartFlow’s cloud platform. Their advanced software analyzes your CT images to assess both plaque and blood flow. It creates a precise 3D model of your coronary arteries and simulates how blood moves through them — a process called FFR<sub>CT</sub>.
        <br/><br/>
        This analysis shows how much each blockage reduces blood flow and identifies how much plaque is present, where it’s located, and whether it may pose a future risk. By combining structural and functional insights, HeartFlow gives you and your doctor a complete understanding of your heart’s health.
      </>,
      img: "img/ct/heartflow-plaque.jpg"
    },
    {
      id: 'how-3',
      title: "Step 3: Review Results with Physician",
      short: "Meet with an Instalab physician with expertise in heart disease prevention to go over your scan in detail. They’ll walk you through where plaque has built up, how it’s affecting blood flow, and what it means for your heart health.",
      full: <>
        Meet with an Instalab physician with expertise in heart disease prevention to go over your scan in detail. They’ll walk you through where plaque has built up, how it’s affecting blood flow, and what it means for your heart health.
        <br/><br/>
        You’ll have time to ask questions, understand your risk, and explore the best next steps — whether that’s lifestyle changes, medications, or further care. Our goal is to give you clarity and confidence, so you can take control of your heart health early.
      </>,
      img: "img/ct/heartflow-doctor-patient.jpg"
    }

  ];

  return (
    <div className="heartflow">
      <CtaVideos
        visible={videosDrawerVisible} 
        onClose={closeVideosDrawer}
        width={900}
        placement="right"
        title={width <= Breakpoint.MD ? "Everything You Need to Know" : "Coronary CTA: Everything You Need to Know"}
      />

      <div className="heartflow-hero">
        <div className="heartflow-section">
          <Paragraph className="heartflow-h1">
            Coronary CT Angiogram with HeartFlow
          </Paragraph>
          <Paragraph className="heartflow-p1">
           A non-invasive heart test that shows where plaque has built up in your arteries, and how much it’s affecting blood flow. Combining high-resolution CT with HeartFlow’s analysis, it gives you and your doctor a complete picture, so you can act before a heart attack happens.
          </Paragraph>
          <Button
            type="primary"
            className="schedule-heartflow-btn"
            onClick={() => {
              PosthogHelper.track(currentUser, `[heartflow] clicked schedule heartflow`, true)
              navigate(`/flow/${FlowType.HEARTFLOW}`)
            }}
          >
            <span className="schedule-heartflow-btn-text">Schedule</span> <ArrowRightOutlined className="schedule-heartflow-btn-icon" />
          </Button>

          <br />

          <Paragraph className="heartflow-quote">
            "Heart disease is the leading killer in the US—but it's treatable, decades before a heart attack. A coronary CTA gives the full picture, detecting both calcified and non-calcified plaque, which can lead to heart attacks or strokes if ruptured. <i>This is life-saving information.</i>"
          </Paragraph>

          <div 
            className="heartflow-hero-img-container"
            onClick={showVideosDrawer}
          >
            <img 
              src="/img/cleerly/hero.jpg" 
              className="heartflow-hero-img" 
            />

            <PlayCircleOutlined className="heartflow-play-icon" />

            <div className="heartflow-video-attribute">
              <Paragraph className="heartflow-video-attribute-name"> Dr. Kimberly Brockenbrough </Paragraph>
              <Paragraph className="heartflow-video-attribute-title"> Cardiac Radiologist </Paragraph>
            </div>
          </div>
        </div>
      </div>
      
      <div className="how-section">
        <div className="heartflow-section">
          <div className="divider-container">
            <Text className="heartflow-h1"> 
              How It Works
            </Text>
          </div>

          {howContent.map((how) => (
            <div key={how.id} className="paragraph-container">
              {how.title && (
                <Paragraph className={`heartflow-p1 bold`}>
                  {how.title}
                </Paragraph>
              )}
              
              <Paragraph className="heartflow-p2">
                {expandedParagraphs[how.id] ? how.full : how.short}
              </Paragraph>
              
              {how.short !== how.full && (
                <Button 
                  type="link" 
                  className="read-more-btn"
                  onClick={() => toggleParagraph(how.id)}
                  icon={expandedParagraphs[how.id] ? <UpOutlined /> : <DownOutlined />}
                >
                  {expandedParagraphs[how.id] ? "Read Less" : "Read More"}
                </Button>
              )}

              <br/>

              {how.img && (
                <img src={how.img} className="heartflow-how-img" />
              )}

              {how.video && (
                <div className="heartflow-how-video-container">
                  <ReactPlayer
                    url={how.video}
                    playing={true}
                    loop={true}
                    muted={true}
                    playsinline={true}
                    controls={false}
                    width="100%"
                    height="auto"
                    className="heartflow-how-video"
                    config={{
                      vimeo: {
                        playerOptions: {
                          responsive: true,
                          background: true
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-section">
        <div className="heartflow-section">
          <div className="divider-container">
            <Text className="heartflow-h1"> 
              Pricing
            </Text>
          </div>
          <Paragraph className="heartflow-p1 pricing-p1">
            We'll help you find the most convenient and affordable testing location near you and handle the appointment scheduling. Enter your location below to search our database.
          </Paragraph>

          <FacilitySearch
            productTypeCodes={[ProductTypeCode.CCTA, ProductTypeCode.HEARTFLOW_CONCIERGE_FEE]}
            flowType={FlowType.HEARTFLOW}
          />
        </div>
      </div>

      {faqs?.length > 0 && (
        <div className="faq-section">
          <div className="heartflow-section">
            <div className="divider-container">
              <Text className="heartflow-h1"> 
              FAQ
            </Text>
          </div>

          <Questions items={faqs} />
          </div>  
        </div>
      )}
    </div>
  );
} 